import { useState } from "react";
import { Loader2, Sparkles } from "lucide-react";
import { ModelForm } from "@/components/create/ModelForm";
import { StatusBadge } from "@/components/create/ModelPicker";
import { cn } from "@/lib/utils";
import { useHfCatalog, usePresets } from "@/lib/studio";

function FlowCard({ flow, cover, active, model, onPick }) {
  return (
    <button type="button" onClick={onPick} data-testid={`workflow-${flow.key}`}
      className={cn("group relative overflow-hidden rounded-2xl border text-left transition-colors", active ? "border-white/50" : "border-white/[0.08] hover:border-white/25")}>
      <div className="relative aspect-[4/3] bg-ink-800">
        {cover && <img src={cover} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
        <span className="absolute left-2.5 top-2.5 rounded-full bg-black/60 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-zinc-100 backdrop-blur">ChatSleek original</span>
        <div className="absolute inset-x-0 bottom-0 p-3">
          <p className="text-sm font-semibold text-white">{flow.title}</p>
          <p className="mt-0.5 line-clamp-2 text-[11px] text-zinc-400">{flow.blurb}</p>
        </div>
      </div>
      <div className="flex items-center justify-between px-3 py-2 text-[11px] text-zinc-500">
        <span className="truncate">{model ? `${model.family} · ${model.workflow}` : "Model not in catalog"}</span>
        {model && <StatusBadge status={model.status} />}
      </div>
    </button>
  );
}

// Original workflows: a visual card per workflow, then the real model form with prompt (and optional references) prefilled.
export function WorkflowGallery({ flows, refsFor, needsRefNote }) {
  const cat = useHfCatalog();
  const presets = usePresets().data?.items || [];
  const [active, setActive] = useState(null);
  if (cat.isLoading) return <div className="flex h-32 items-center justify-center text-zinc-500"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  const byId = Object.fromEntries((cat.data?.models || []).map((m) => [m.id, m]));
  const flow = flows.find((f) => f.key === active);
  const model = flow && byId[flow.model];
  return (
    <div className="space-y-4" data-testid="workflow-gallery">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {flows.map((f) => <FlowCard key={f.key} flow={f} model={byId[f.model]} active={active === f.key} cover={presets.find((p) => p.slug === f.cover)?.cover_url} onPick={() => setActive(f.key)} />)}
      </div>
      <p className="flex items-center gap-1.5 text-[11px] text-zinc-600"><Sparkles className="h-3 w-3" /> Previews were made with ChatSleek Image. Official Higgsfield templates aren't available through the API.</p>
      {flow && model && model.status === "available" && (
        <div className="space-y-3 rounded-[22px] border border-white/[0.08] bg-ink-850 p-4 sm:p-5" data-testid="workflow-form">
          <h3 className="text-sm font-semibold text-white">{flow.title} · {model.family}</h3>
          {needsRefNote && flow.field && !refsFor(flow) && <p className="text-xs text-amber-200/80">{needsRefNote}</p>}
          <ModelForm key={`${flow.key}-${refsFor(flow)?.[0]?.id || "none"}`} model={model} initial={{ prompt: flow.prompt }}
            presetRefs={flow.field && refsFor(flow) ? { [flow.field]: refsFor(flow) } : {}} />
        </div>
      )}
      {flow && model && model.status !== "available" && <p className="text-sm text-zinc-400" data-testid="workflow-unavailable">{model.status_reason}</p>}
    </div>
  );
}
